import { MfaFactor, SignInIdentifier, type VerificationCodeIdentifier } from '@logto/schemas';
import { t } from 'i18next';
import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { validate } from 'superstruct';

import SecondaryPageLayout from '@/Layout/SecondaryPageLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { type IdentifierInputValue } from '@/components/InputFields/SmartInputField';
import VerificationCodeContainer from '@/containers/VerificationCode';
import { useSieMethods } from '@/hooks/use-sie';
import ErrorPage from '@/pages/ErrorPage';
import { UserFlow } from '@/types';
import { userFlowGuard } from '@/types/guard';
import { formatPhoneNumberWithCountryCallingCode } from '@/utils/country-code';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

type Parameters = {
  flow: string;
};

const isValidVerificationCodeIdentifier = (
  identifierInputValue: IdentifierInputValue | undefined
): identifierInputValue is VerificationCodeIdentifier =>
  Boolean(
    identifierInputValue?.type &&
      identifierInputValue.type !== SignInIdentifier.Username &&
      identifierInputValue.value
  );

const VerificationCode = () => {
  const { flow } = useParams<Parameters>();
  const { signInMethods } = useSieMethods();
  const { experienceSettings } = useContext(PageContext);

  const { identifierInputValue, forgotPasswordIdentifierInputValue, verificationIdsMap } =
    useContext(UserInteractionContext);

  const [, userFlow] = validate(flow, userFlowGuard);

  if (!userFlow) {
    console.warn('[VerificationCode] 404: invalid userFlow', { flow });
    return <ErrorPage />;
  }

  const cachedIdentifierInputValue =
    flow === UserFlow.ForgotPassword ? forgotPasswordIdentifierInputValue : identifierInputValue;

  if (!isValidVerificationCodeIdentifier(cachedIdentifierInputValue)) {
    // Avoid logging the identifier value (email/phone) — record only that the
    // session identifier was missing/invalid.
    console.warn('[VerificationCode] invalid_session: missing or invalid identifier');
    return <ErrorPage title="error.invalid_session" />;
  }

  const { type, value } = cachedIdentifierInputValue;

  // SignIn Method not enabled
  const methodSettings = signInMethods.find((method) => method.identifier === type);
  // Allow phone identifier when used as an MFA factor (SMS / WhatsApp), even if
  // phone isn't enabled as a sign-in method for the tenant.
  const isPhoneMfaFactor =
    type === SignInIdentifier.Phone &&
    Boolean(
      experienceSettings?.mfa.factors.some(
        (factor) => factor === MfaFactor.SMS || factor === MfaFactor.WA
      )
    );

  if (!methodSettings && !isPhoneMfaFactor && flow !== UserFlow.ForgotPassword) {
    console.warn('[VerificationCode] 404: method not enabled', {
      type,
      methodSettings,
      isPhoneMfaFactor,
      mfaFactors: experienceSettings?.mfa.factors,
    });
    return <ErrorPage />;
  }

  // VerificationId not found
  const verificationId = verificationIdsMap[codeVerificationTypeMap[type]];

  if (!verificationId) {
    console.warn('[VerificationCode] invalid_session: no verificationId', {
      type,
      verificationIdsMap,
    });
    return <ErrorPage title="error.invalid_session" rawMessage="Verification ID not found" />;
  }

  return (
    <SecondaryPageLayout
      title={`description.verify_${type}`}
      description="description.enter_passcode"
      descriptionProps={{
        address: t(`description.${type === SignInIdentifier.Email ? 'email' : 'phone_number'}`),
        target:
          type === SignInIdentifier.Phone ? formatPhoneNumberWithCountryCallingCode(value) : value,
      }}
    >
      <VerificationCodeContainer
        flow={userFlow}
        identifier={cachedIdentifierInputValue}
        verificationId={verificationId}
        hasPasswordButton={userFlow === UserFlow.SignIn && methodSettings?.password}
      />
    </SecondaryPageLayout>
  );
};

export default VerificationCode;
